import React from 'react'
import { BaseText } from '@/components/base'
import { currencyUtils } from '@/utils'
import type { CryptoCode } from '@/constants/currencyCodes'

const { currencyConditionals, formatDynamicCurrency } = currencyUtils

interface LabelAmountProps {
  amount: string
  currencyCode?: CryptoCode
  currencyLabel?: string
  centralize?: boolean
  shouldMaskStablecoinAsFiat?: boolean
  showDecimals?: boolean
  shouldRemoveTrailingZeros?: boolean
  style?: React.CSSProperties
}

export const LabelAmount: React.FC<LabelAmountProps> = ({
  amount,
  currencyCode,
  currencyLabel,
  centralize = false,
  shouldMaskStablecoinAsFiat = false,
  showDecimals = false,
  shouldRemoveTrailingZeros = false,
  style,
}) => {
  const { isFiduciary, isStablecoin, isBtc, isGold, isUsdt } =
    currencyConditionals(currencyCode)

  const formatAmount = (): string => {
    if (isFiduciary || shouldMaskStablecoinAsFiat) {
      const fiatCode = isStablecoin ? 'USD' : 'USD' // Simplified version

      return formatDynamicCurrency({
        amount: Number(amount),
        currencyCode: fiatCode,
        showDecimals,
        shouldRemoveTrailingZeros,
      })
    }

    // Format amount to max 6 decimal places
    const formatCryptoAmount = (value: string): string => {
      const numValue = parseFloat(value)
      if (isNaN(numValue)) return '0'

      // Check if number has decimals
      const hasDecimals = value.includes('.')
      if (!hasDecimals) return value

      // Limit to 6 decimal places
      const [integerPart, decimalPart] = value.split('.')
      if (decimalPart && decimalPart.length > 6) {
        return `${integerPart}.${decimalPart.slice(0, 6)}`
      }

      return value
    }

    const formattedAmount = formatCryptoAmount(amount)

    if (isBtc) {
      return `₿${formattedAmount}`
    }

    if (isGold) {
      return `${formattedAmount} GOLD`
    }

    if (isUsdt) {
      return `${formattedAmount} USDT`
    }

    return `${formattedAmount} ${currencyLabel || currencyCode || ''}`
  }

  return (
    <BaseText
      lineHeight={60}
      fontSize={44}
      fontFamily="semibold"
      color="label.default"
      textAlign={centralize ? 'center' : 'left'}
      style={style}
    >
      {formatAmount()}
    </BaseText>
  )
}

export default LabelAmount
